import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { useUsuario } from '@/hooks/useUsuario';
import { api } from '@/lib/api';
import { pedirAtencion, useAtencion } from '@/lib/atencion';
import type { ActividadReciente, DashboardData } from '@/lib/types';
import { ZONE_LABELS, classNames } from '@/lib/utils';
import BloquePendiente from '@/components/dashboard/BloquePendiente';
import BloqueNegocio from '@/components/dashboard/BloqueNegocio';

/**
 * La entrada del CRM: lo que hay que hacer (Pendiente) y cómo va el mes (El
 * negocio). Lo pendiente sale de GET /api/atencion, lo mismo que los números
 * del menú; el negocio se pide aparte, después, porque pesa más.
 */

function saludo(hora: number): string {
  if (hora >= 6 && hora < 14) return 'Buenos días';
  if (hora >= 14 && hora < 21) return 'Buenas tardes';
  return 'Buenas noches';
}

/** El nombre de pila, si el inicio de sesión lo trae; una cuenta de correo no es un nombre. */
function nombreDePila(nombre?: string, cuenta?: string): string | null {
  const n = (nombre || '').trim() || (cuenta && !cuenta.includes('@') ? cuenta.trim() : '');
  return n ? n.split(/\s+/)[0] : null;
}

function horaCorta(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

/** «16:40» si es de hoy, «Ayer», o «21 oct». */
function cuando(iso: string): string {
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 1);
  if (d.toDateString() === hoy.toDateString()) return horaCorta(iso);
  if (d.toDateString() === ayer.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '');
}

const TIPO_FICHA: Record<string, { etiqueta: string; ruta: (id: string) => string }> = {
  lead: { etiqueta: 'Lead', ruta: (id) => `/leads/propietarios?lead=${encodeURIComponent(id)}` },
  owner: { etiqueta: 'Propietario', ruta: (id) => `/owners/${encodeURIComponent(id)}` },
  property: { etiqueta: 'Piso', ruta: (id) => `/properties/${encodeURIComponent(id)}` },
  candidato: { etiqueta: 'Candidato', ruta: (id) => `/equipo/candidatos/${encodeURIComponent(id)}` },
  empleado: { etiqueta: 'Personal', ruta: (id) => `/equipo/personal/${encodeURIComponent(id)}` },
};

function textoActividad(a: ActividadReciente): string {
  if (a.activityType === 'stage_change' && a.faseNombre) return `Lead movido a «${a.faseNombre}»`;
  return a.title || a.description?.split('\n')[0] || 'Actividad';
}

function FilaActividad({ a }: { a: ActividadReciente }) {
  const ficha = a.entityType ? TIPO_FICHA[a.entityType] : undefined;
  const contenido = (
    <>
      <span className="text-xs text-sereno-dark/60 tabular-nums pt-px">{cuando(a.createdAt)}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium truncate">{textoActividad(a)}</span>
        {(a.entidadNombre || ficha) && (
          <span className="block text-xs text-sereno-dark/60 truncate">
            {[ficha?.etiqueta, a.entidadNombre].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
    </>
  );
  const clase = 'grid grid-cols-[52px_minmax(0,1fr)] gap-2 py-1.5 px-2 -mx-2 rounded-lg';
  return ficha && a.entityId ? (
    <Link to={ficha.ruta(a.entityId)} className={classNames(clase, 'hover:bg-sereno-blue/20 transition-colors')}>{contenido}</Link>
  ) : (
    <div className={clase}>{contenido}</div>
  );
}

export default function DashboardPage() {
  const usuario = useUsuario();
  const { datos: atencion, cargando, error } = useAtencion();
  const dashboard = useApi<DashboardData>(() => api.getDashboard(), []);
  // Al pulsar «Actualizar» se vuelve a pedir también El negocio
  const [vuelta, setVuelta] = useState(0);

  // El menú también la pide al montar: el almacén junta las dos en una
  useEffect(() => { void pedirAtencion(false); }, []);

  const actualizar = () => {
    void pedirAtencion(true);
    dashboard.refresh();
    setVuelta((v) => v + 1);
  };

  const ahora = new Date();
  const fecha = ahora.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  const nombre = nombreDePila(usuario?.nombre, usuario?.userDetails);
  const data = dashboard.data;
  const propietarios = data ? data.activeOwners ?? data.totalOwners : 0;

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-sereno-dark/60 first-letter:uppercase">{fecha}</p>
          <h1 className="text-2xl font-bold">{saludo(ahora.getHours())}{nombre ? `, ${nombre}` : ''}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {atencion && <span className="text-xs text-sereno-dark/60">Actualizado a las {horaCorta(atencion.generadoEn)}</span>}
          <button
            type="button"
            onClick={actualizar}
            disabled={cargando}
            className="btn-secondary inline-flex items-center gap-2 min-h-[40px] text-sm disabled:opacity-60"
          >
            <RefreshCw size={16} className={cargando ? 'animate-spin' : ''} aria-hidden="true" />
            Actualizar
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
        <BloquePendiente
          className="xl:col-span-7"
          atencion={atencion}
          cargando={cargando}
          error={error}
          onReintentar={() => void pedirAtencion(true)}
        />
        <BloqueNegocio className="xl:col-span-5" vuelta={vuelta} />
      </div>

      {/* La de siempre, ya arreglada: las últimas, cambios de fase incluidos */}
      <section aria-labelledby="t-actividad" className="card !p-5 sm:!p-6">
        <h2 id="t-actividad" className="mb-2 text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Actividad reciente</h2>
        {dashboard.loading && !data ? (
          <p className="text-sm text-sereno-dark/50">Cargando…</p>
        ) : !data ? (
          <p className="text-sm text-sereno-dark/50">No se ha podido cargar la actividad.</p>
        ) : data.recentActivities.length === 0 ? (
          <p className="text-sm text-sereno-dark/50">Sin actividad reciente.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
            {data.recentActivities.map((a) => <FilaActividad key={a.id} a={a} />)}
          </div>
        )}
      </section>

      {data && (
        <footer className="text-xs text-sereno-dark/60 leading-relaxed">
          <span className="font-semibold text-sereno-dark">Cartera:</span>{' '}
          {data.totalProperties} {data.totalProperties === 1 ? 'casa' : 'casas'} y {propietarios} {propietarios === 1 ? 'propietario' : 'propietarios'}
          {data.propertiesByZone.map((z) => (
            <span key={z.zone}> · {ZONE_LABELS[z.zone] || z.zone} {z.count}</span>
          ))}
        </footer>
      )}
    </div>
  );
}
